import React, { useEffect, useMemo } from 'react';

import {
  App,
  BankAccountDetail,
  BankAccountDetailsUpdateRequest,
  RestApiErrorResult,
  StripeConnectedAccountInfo,
} from '@flipdish/api-client-typescript';
import { Badge } from '@flipdish/ui-library/components/atoms/Badge';
import { Button } from '@flipdish/ui-library/components/atoms/Button';
import { Divider } from '@flipdish/ui-library/components/atoms/Divider';
import { Input } from '@flipdish/ui-library/components/atoms/Input';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import Permissions from 'react-redux-permissions';
import { type RouteComponentProps } from 'react-router';
import { compose } from 'recompose';

import { FeatureFlag } from '@fd/ui/FeatureFlag';
import { getSalesChannelType } from '@fd/ui/utils/generateSalesChannelsTypes';

import useTailwindPortal from '../../../../../custom-hooks/useTailwindPortal';
import {
  notify,
  notifyError,
  NotifyProps,
  notifySaved,
} from '../../../../../layouts/Notify/actions';
import { editBankAccountFeature } from '../../../../../selectors/app.selector';
import { bankingServices } from '../../../../../services/banking.service';
import { PortalFeatureFlags } from '../../../../../services/featureFlags/portalFeatureFlags';
import { useFeatureFlagValue } from '../../../../../services/featureFlags/useFeatureFlagValue';
import { flagService } from '../../../../../services/flagService';
import useStoreToSalesChannelTransformerWithQuery from '../../../../Settings/utils/useStoreToSalesChannelTransformerWithQuery';
import * as bankingActions from '../../banking.actions';
import { countryCodeToCountry, getAccountByRouteId } from '../../banking.selectors';
import { getDynamicLabel } from '../../components/bankAccFieldTextHelpers/DynamicLabel';
import formatDynamicValue from '../../components/bankAccFieldTextHelpers/DynamicValue';
import VerifyYourDetails from '../../components/VerifyYourDetails';
import { nonVATSupportedCountries } from '../../constants';
import { isPendingVerificationAdditionalInfoRequired } from '../../utils';
import DetailRow from './components/DetailRow';
import DetailSection from './components/DetailSection';
import EditAccountButton from './components/EditAccountButton';
import LoadingSkeleton from './components/LoadingSkeleton';
import { ManualPayoutVerification } from './components/ManualPayoutVerification';
import PageHeader from './components/PageHeader';
import PayoutAlertBanner from './components/PayoutAlertBanner';
import { PayoutIntervalSelector } from './components/PayoutIntervalSelector';
import RemoveAccountButton from './components/RemoveAccountButton';
import StaffAccountActions from './components/StaffAccountActions';
import StripeDetailsButton from './components/StripeDetailsButton';

type Props = RouteComponentProps<{ appId: string; accountId: string }>;

const BankingDetailsV2 = (props: Props & MappedState & MappedDispatch) => {
  const {
    appId,
    account,
    editBankAccountFeature,
    getBankAccountDetails,
    removeBankAccount,
    dispatchNotifyError,
    dispatchNotifySaved,
    allowUpdateToBankingVatNumber,
    translate,
  } = props;

  useTailwindPortal();

  const [vatNumber, setVatNumber] = React.useState('');
  const isLoading = account === undefined;

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (variables: { accountId: number; account: BankAccountDetailsUpdateRequest }) =>
      bankingServices.updateBankAccount(appId, variables.accountId, variables.account),

    onSuccess: () => {
      dispatchNotifySaved();
    },
    onError: (error: AxiosError) => {
      if (error instanceof AxiosError) {
        const apiErrorMessage = error.response?.data as RestApiErrorResult;
        dispatchNotifyError({ message: apiErrorMessage.Message });
      } else {
        dispatchNotifyError({ message: 'Something_went_wrong' });
      }
    },
  });

  const isStripeCountryAndVerified = Boolean(
    account?.StripeConnectedAccountInfo &&
    [
      BankAccountDetail.AccountStateEnum.Verified,
      BankAccountDetail.AccountStateEnum.Grandfathered,
    ].includes(account.AccountState)
  );
  const stripeStatus = account?.StripeConnectedAccountInfo?.AccountStatus;
  const accountStatus = account?.AccountState;
  const pendingVerificationAsAdditionalInfoEnabled = useFeatureFlagValue(
    PortalFeatureFlags.bankAccount_pendingVerificationAsAdditionalInfo,
    false
  );
  const showStripeAlert =
    account &&
    account.StripeConnectedAccountInfo &&
    account.StripeConnectedAccountInfo.AccountStatus ===
      StripeConnectedAccountInfo.AccountStatusEnum.UpdateExternalAccount;
  const showVerifyStripeDetails =
    account?.AccountState !== BankAccountDetail.AccountStateEnum.Unverified &&
    (stripeStatus === StripeConnectedAccountInfo.AccountStatusEnum.Unverified ||
      stripeStatus === StripeConnectedAccountInfo.AccountStatusEnum.AdditionalInformationRequired ||
      (pendingVerificationAsAdditionalInfoEnabled &&
        isPendingVerificationAdditionalInfoRequired(account?.StripeConnectedAccountInfo)));
  const showEditStripeAccountDetails = account && stripeStatus && !showVerifyStripeDetails;
  const showVerifyActions =
    account && account.AccountState === BankAccountDetail.AccountStateEnum.AwatingVerification;
  const showEditButton =
    stripeStatus !== StripeConnectedAccountInfo.AccountStatusEnum.Rejected &&
    stripeStatus !== StripeConnectedAccountInfo.AccountStatusEnum.Disabled &&
    accountStatus !== BankAccountDetail.AccountStateEnum.Unverified;

  const storeIdAssociations = useStoreToSalesChannelTransformerWithQuery();
  const accountStoreNames = useMemo(() => {
    const associatedStoreNames = account?.StoreIds?.map((storeId) => {
      const association = storeIdAssociations.find(
        (association) => association.storeId === storeId
      );
      return association?.salesChannelName
        ? `${association?.salesChannelName}${getSalesChannelType(association?.salesChannelType ?? '')}`
        : '';
    });
    const removedEmptyStoreNames = associatedStoreNames?.filter((name) => name);
    if ((removedEmptyStoreNames || [])?.length > 0) {
      return removedEmptyStoreNames;
    }
    return account?.StoreNames || [];
  }, [account, storeIdAssociations]);

  useEffect(() => {
    getBankAccountDetails();
  }, []);

  useEffect(() => {
    if (account) {
      setVatNumber(account?.VatNumber);
    }
  }, [account]);

  const handleSaveVatNumber = () => {
    mutateAsync({
      accountId: account.Id,
      account: { ...account, VatNumber: vatNumber },
    });
  };

  const showVatNumberField = !nonVATSupportedCountries.includes(account?.AccountHolderCountryCode);

  return (
    <div data-tailwind className="min-h-screen bg-white">
      <div className="mx-auto flex max-w-[1280px] flex-col gap-6 px-4 sm:px-8 pt-8 pb-10">
        <PageHeader appId={appId} accountName={account?.AccountName ?? 'Bank Account'} />

        {isLoading && <LoadingSkeleton />}
        <Permissions allowed={[App.AppResourceSetEnum.ViewBankAccounts]}>
          <VerifyYourDetails account={account} showVerifyStripeDetails={showVerifyStripeDetails} />
        </Permissions>
        {editBankAccountFeature && showStripeAlert && <PayoutAlertBanner />}

        {account != undefined && (
          <div className="flex flex-col items-end gap-5 py-6">
            {/* Bank account */}
            <DetailSection title={<Translate id="Bank_account" />}>
              <DetailRow label={<Translate id="Account_name" />} value={account?.AccountName} />
              <DetailRow label={<Translate id="Account_type" />} value={account?.BusinessType} />
              <DetailRow label={<Translate id="Bank_name" />} value={account?.BankName} />
              <DetailRow label={<Translate id="Address" />} value={account?.BankAddress} />
              <DetailRow
                label={<Translate id="Bank_country" />}
                value={countryCodeToCountry(account?.BankCountryCode)}
              />
              {account.PopulatedAccountFields?.map((field) => {
                const translatedKey = getDynamicLabel(field.Key);
                return (
                  <DetailRow
                    key={field.Key}
                    label={<Translate id={translatedKey} />}
                    value={formatDynamicValue(field.Value, translatedKey)}
                  />
                );
              })}
            </DetailSection>

            <Divider />

            {/* Account holder address */}
            <DetailSection title={<Translate id="Account_holder_address" />}>
              <DetailRow label={<Translate id="Address" />} value={account?.AccountHolderAddress} />
              <DetailRow
                label={<Translate id="Country" />}
                value={countryCodeToCountry(account?.AccountHolderCountryCode)}
              />
            </DetailSection>

            <Divider />

            {/* VAT number */}
            {showVatNumberField && (
              <>
                <div className="flex flex-col sm:flex-row w-full items-start gap-4 sm:gap-x-8">
                  <div className="w-full sm:min-w-[240px] sm:max-w-[280px] sm:shrink-0">
                    <span className="text-base font-bold text-secondary">
                      <Translate id="Vat_details" />
                    </span>
                  </div>
                  <div className="w-full sm:flex-1">
                    <Input
                      label={translate('Vat_number_EU_only') as string}
                      value={vatNumber}
                      onChange={(val) => setVatNumber(val)}
                      placeholder={translate('Enter_VAT_number') as string}
                    />
                  </div>
                </div>

                <Divider />
              </>
            )}

            {/* Associated sales channels */}
            <DetailSection title={<Translate id="Associated_sales_channels" />}>
              <span className="text-base text-tertiary">
                {accountStoreNames.length > 0 ? (
                  accountStoreNames.join(', ')
                ) : (
                  <Translate id="No_associated_stores" />
                )}
              </span>
            </DetailSection>

            <Divider />

            {/* Payouts */}
            {showEditStripeAccountDetails && (
              <>
                <div className="flex flex-col sm:flex-row w-full items-start gap-4 sm:gap-x-8">
                  <div className="w-full sm:min-w-[240px] sm:max-w-[280px] sm:shrink-0">
                    <span className="text-base font-bold text-secondary">
                      <Translate id="Payouts" />
                    </span>
                  </div>
                  <div className="flex w-full sm:flex-1 flex-col items-start gap-2">
                    <StripeDetailsButton
                      bankAccountId={account.Id}
                      stripeAccount={account.StripeConnectedAccountInfo!}
                    />
                    <div className="flex flex-col gap-2 text-base text-tertiary">
                      <p>
                        <Translate id="We_use_Stripe_helptext" />
                      </p>
                      <p>
                        <Translate id="Youll_be_redirected_to_Stripe_helptext" />
                      </p>
                    </div>
                  </div>
                </div>

                <Divider />
              </>
            )}

            {/* Save VAT details */}
            {allowUpdateToBankingVatNumber && showVatNumberField && (
              <Permissions allowed={['Owner', 'ManagedOwner', 'FlipdishStaff']}>
                <>
                  <Button
                    color="secondary"
                    size="md"
                    onPress={handleSaveVatNumber}
                    isDisabled={isPending}
                    className="w-full sm:w-auto"
                  >
                    <Translate id="Save_VAT_details" />
                  </Button>

                  <Divider />
                </>
              </Permissions>
            )}

            {/* Flipdish staff section */}
            <Permissions allowed={['FlipdishStaff']}>
              <div className="flex flex-col sm:flex-row w-full sm:items-end gap-4">
                <div className="w-full sm:flex-1 sm:min-w-0">
                  <Badge color="brand">
                    <Translate id="Flipdish_staff_only" />
                  </Badge>
                </div>

                {showVerifyActions && <StaffAccountActions accountId={account.Id as number} />}

                {isStripeCountryAndVerified && (
                  <>
                    <div className="order-2 sm:order-1 w-full sm:w-auto">
                      <ManualPayoutVerification
                        stripeAccount={account.StripeConnectedAccountInfo!}
                        appId={appId}
                        getBankAccountDetails={getBankAccountDetails}
                      />
                    </div>
                    <div className="order-1 sm:order-2 w-full sm:w-auto">
                      <PayoutIntervalSelector
                        stripeAccount={account.StripeConnectedAccountInfo!}
                        appId={appId}
                        getBankAccountDetails={getBankAccountDetails}
                      />
                    </div>
                  </>
                )}

                <FeatureFlag flag="edit-bank-account">
                  {showEditButton && <EditAccountButton accountId={account.Id as number} />}
                </FeatureFlag>
              </div>

              <Divider />
            </Permissions>

            {/* Remove account */}
            <div className="flex w-full">
              <RemoveAccountButton
                handleRemove={removeBankAccount}
                storeNames={account.StoreNames}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

type MappedState = ReturnType<ReturnType<typeof mapStateToPropsFactory>>;
const mapStateToPropsFactory = (initialState, ownProps) => {
  const { locale } = initialState;
  return (state: AppState) => {
    return {
      appId: state.currentApp.AppId!,
      account: getAccountByRouteId(state, ownProps.match.params.accountId),
      editBankAccountFeature: editBankAccountFeature(state),
      translate: getTranslate(locale),
      allowUpdateToBankingVatNumber: flagService.isFlagOn(state, 'allowUpdateToBankingVatNumber'),
    };
  };
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch, ownProps: Props) => ({
  getBankAccountDetails: () => {
    dispatch(bankingActions.getBankAccountDetails(ownProps.match.params.accountId));
  },
  removeBankAccount: () =>
    dispatch(bankingActions.removeBankAccount(Number(ownProps.match.params.accountId))).then(() => {
      ownProps.history.replace(`/${ownProps.match.params.appId}/finance/bank-accounts/`);
    }),
  notify: (data: NotifyProps) => dispatch(notify(data)),
  dispatchNotifyError: (data: NotifyProps) => dispatch(notifyError(data)),
  dispatchNotifySaved: () => dispatch(notifySaved()),
});

export default compose<Props, RouteComponentProps<{ appId: string; accountId: string }>>(
  connect(mapStateToPropsFactory, mapDispatchToProps)
)(BankingDetailsV2);
